import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarOff, Receipt } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/lib/requests";
import { LeaveRow, ReimbRow } from "@/components/requests/RequestRows";
import LeaveDialog from "@/components/requests/LeaveDialog";
import ReimbursementDialog from "@/components/requests/ReimbursementDialog";
import DecisionDialog from "@/components/requests/DecisionDialog";

function Section({ title, empty, children, testid }) {
  const list = React.Children.toArray(children);
  return (
    <div className="space-y-2.5" data-testid={testid}>
      <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
      {list.length ? list : <p className="text-sm text-slate-400 py-4">{empty}</p>}
    </div>
  );
}

export default function RequestsPage({ embedded = false }) {
  const { user } = useAuth();
  const cur = useCurrency();
  const approver = user.is_hr || user.is_owner;
  const [mine, setMine] = useState({ leaves: [], reimbs: [] });
  const [all, setAll] = useState({ leaves: [], reimbs: [] });
  const [dialog, setDialog] = useState(null);
  const [action, setAction] = useState(null);

  const load = useCallback(async () => {
    const [l, r] = await Promise.all([api.get("/leaves"), api.get("/reimbursements")]);
    setMine({ leaves: l.data, reimbs: r.data });
    if (approver) {
      const [al, ar] = await Promise.all([api.get("/leaves", { params: { scope: "all" } }), api.get("/reimbursements", { params: { scope: "all" } })]);
      setAll({ leaves: al.data, reimbs: ar.data });
    }
  }, [approver]);
  useEffect(() => { load(); }, [load]);

  const cancel = (kind) => async (item) => {
    if (!window.confirm("Cancel this request?")) return;
    try { await api.delete(`/${kind}/${item.id}`); toast.success("Request cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };
  const act = (kind) => (item, mode) => setAction({ item, kind, mode });
  const common = { me: user, cur };
  const pendingL = all.leaves.filter((x) => x.status === "pending");
  const pendingR = all.reimbs.filter((x) => x.status === "pending");

  return (
    <div className={embedded ? "" : "p-4 sm:p-6 lg:p-8 max-w-[1100px]"} data-testid="requests-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          {!embedded && <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Leave &amp; Reimbursements</h1>}
          <p className="text-sm text-slate-500 mt-1">Submit requests and track their status.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setDialog("leave")} variant="outline" data-testid="new-leave-button"><CalendarOff className="w-4 h-4 mr-1.5" />Request leave</Button>
          <Button onClick={() => setDialog("reimb")} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-reimbursement-button"><Receipt className="w-4 h-4 mr-1.5" />Reimbursement</Button>
        </div>
      </div>

      <Tabs defaultValue={approver && (pendingL.length || pendingR.length) ? "approvals" : "mine"}>
        <TabsList className="mb-4 max-w-full overflow-x-auto">
          <TabsTrigger value="mine" data-testid="requests-mine-tab">My requests</TabsTrigger>
          {approver && <TabsTrigger value="approvals" data-testid="requests-approvals-tab">Approvals ({pendingL.length + pendingR.length})</TabsTrigger>}
          {approver && <TabsTrigger value="history" data-testid="requests-history-tab">All history</TabsTrigger>}
        </TabsList>

        <TabsContent value="mine" className="space-y-8">
          <Section title="My leave" empty="No leave requests yet." testid="my-leaves">
            {mine.leaves.map((x) => <LeaveRow key={x.id} item={x} {...common} onCancel={cancel("leaves")} />)}
          </Section>
          <Section title="My reimbursements" empty="No reimbursement requests yet." testid="my-reimbursements">
            {mine.reimbs.map((x) => <ReimbRow key={x.id} item={x} {...common} onCancel={cancel("reimbursements")} />)}
          </Section>
        </TabsContent>

        {approver && (
          <TabsContent value="approvals" className="space-y-8">
            <Section title="Pending leave" empty="No pending leave requests." testid="pending-leaves">
              {pendingL.map((x) => <LeaveRow key={x.id} item={x} showUser canDecide {...common} onAct={act("leaves")} />)}
            </Section>
            <Section title="Pending reimbursements" empty="No pending reimbursements." testid="pending-reimbursements">
              {pendingR.map((x) => <ReimbRow key={x.id} item={x} showUser canDecide {...common} onAct={act("reimbursements")} />)}
            </Section>
          </TabsContent>
        )}

        {approver && (
          <TabsContent value="history" className="space-y-8">
            <Section title="All leave" empty="Nothing yet." testid="all-leaves">
              {all.leaves.map((x) => <LeaveRow key={x.id} item={x} showUser canDecide {...common} onAct={act("leaves")} />)}
            </Section>
            <Section title="All reimbursements" empty="Nothing yet." testid="all-reimbursements">
              {all.reimbs.map((x) => <ReimbRow key={x.id} item={x} showUser canDecide canClose={user.is_owner} {...common} onAct={act("reimbursements")} />)}
            </Section>
          </TabsContent>
        )}
      </Tabs>

      {dialog === "leave" && <LeaveDialog onClose={() => setDialog(null)} onCreated={load} />}
      {dialog === "reimb" && <ReimbursementDialog onClose={() => setDialog(null)} onCreated={load} />}
      {action && <DecisionDialog {...action} onClose={() => setAction(null)} onDone={load} />}
    </div>
  );
}
